import { AlertOctagon, BarChart3, Bell, Database, Package, ShieldAlert, TimerReset, Workflow } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { DashboardSummary, HealthMapEdge, HealthMapNode, IntegrationEvent, IntegrationJob, IntegrationRun } from "../../shared/types";
import { MetricCard } from "../components/dashboard/MetricCard";
import { MonitoringPanel } from "../components/dashboard/MonitoringPanel";
import { SeverityBadge } from "../components/common/SeverityBadge";
import { StateBlock } from "../components/common/StateBlock";
import { KineticTextLoader } from "../components/common/KineticTextLoader";
import { StatusChip } from "../components/common/StatusChip";
import { HealthMap } from "../components/health-map/HealthMap";
import { getDashboardSummary } from "../services/dashboardService";
import { getEvents } from "../services/eventService";
import { getHealthMap } from "../services/healthMapService";
import { getJobs } from "../services/jobService";
import { getRuns } from "../services/runService";
import { formatDateTime } from "../utils/status";

interface DashboardHomeProps {
  refreshKey: number;
  onOpenJob: (jobId: string) => void;
  onOpenRuns: () => void;
}

function latestTimestamp(values: Array<string | undefined>): string | undefined {
  const sortedValues = values.filter(Boolean).sort();
  return sortedValues[sortedValues.length - 1];
}

export function DashboardHome({ refreshKey, onOpenJob, onOpenRuns }: DashboardHomeProps) {
  const [summary, setSummary] = useState<DashboardSummary>();
  const [jobs, setJobs] = useState<IntegrationJob[]>([]);
  const [runs, setRuns] = useState<IntegrationRun[]>([]);
  const [events, setEvents] = useState<IntegrationEvent[]>([]);
  const [healthMapNodes, setHealthMapNodes] = useState<HealthMapNode[]>([]);
  const [healthMapEdges, setHealthMapEdges] = useState<HealthMapEdge[]>([]);
  const [isHealthMapLoading, setIsHealthMapLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let isMounted = true;
    let healthMapTimer: number | undefined;
    setIsLoading(true);
    setError(undefined);
    setHealthMapNodes([]);
    setHealthMapEdges([]);
    setIsHealthMapLoading(false);

    Promise.all([getDashboardSummary(), getJobs(), getRuns(), getEvents()])
      .then(([nextSummary, nextJobs, nextRuns, nextEvents]) => {
        if (!isMounted) {
          return;
        }
        const activeJobs = nextJobs.filter((job) => job.isActive);
        const count = (status: IntegrationJob["status"]) => activeJobs.filter((job) => job.status === status).length;
        // Use the same job snapshot for the fleet totals and source panels.
        setSummary({
          ...nextSummary,
          totalJobs: activeJobs.length,
          healthyJobs: count("SUCCEEDED") + count("SCHEDULED"),
          runningJobs: count("RUNNING"),
          failedJobs: count("FAILED"),
          missingJobs: count("MISSING"),
          lateJobs: count("LATE"),
          longRunningJobs: count("LONG_RUNNING"),
          cancelledJobs: count("CANCELLED"),
          unknownJobs: count("UNKNOWN")
        });
        setJobs(activeJobs);
        setRuns(nextRuns);
        setEvents(nextEvents);

        healthMapTimer = window.setTimeout(() => {
          if (!isMounted) {
            return;
          }

          setIsHealthMapLoading(true);
          getHealthMap()
            .then((nextHealthMap) => {
              if (!isMounted) {
                return;
              }
              setHealthMapNodes(nextHealthMap.nodes);
              setHealthMapEdges(nextHealthMap.edges);
            })
            .catch(() => {
              if (!isMounted) {
                return;
              }
              setHealthMapNodes([]);
              setHealthMapEdges([]);
            })
            .finally(() => {
              if (isMounted) {
                setIsHealthMapLoading(false);
              }
            });
        }, 160);
      })
      .catch((err: Error) => {
        if (isMounted) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
      if (healthMapTimer !== undefined) {
        window.clearTimeout(healthMapTimer);
      }
    };
  }, [refreshKey]);

  const incidentJobs = useMemo(
    () => jobs.filter((job) => ["FAILED", "MISSING", "LATE", "LONG_RUNNING", "CANCELLED", "UNKNOWN"].includes(job.status)),
    [jobs]
  );

  const recentRuns = useMemo(() => runs.slice(0, 6), [runs]);
  const synapseJobs = useMemo(() => jobs.filter((job) => job.sourceType === "SYNAPSE"), [jobs]);
  const synapseRuns = useMemo(() => runs.filter((run) => run.sourceType === "SYNAPSE"), [runs]);
  const acrJobs = useMemo(() => jobs.filter((job) => job.sourceType === "ACR"), [jobs]);
  const acrRuns = useMemo(() => runs.filter((run) => run.sourceType === "ACR"), [runs]);
  const acrEvents = useMemo(() => events.filter((event) => event.sourceType === "ACR"), [events]);
  const dbJobs = useMemo(() => jobs.filter((job) => job.sourceType === "SQL_ETL"), [jobs]);
  const dbRuns = useMemo(() => runs.filter((run) => run.sourceType === "SQL_ETL"), [runs]);
  const dbEvents = useMemo(() => events.filter((event) => event.sourceType === "SQL_ETL"), [events]);
  const powerBiJobs = useMemo(() => jobs.filter((job) => job.sourceType === "POWER_BI"), [jobs]);
  const powerBiRuns = useMemo(() => runs.filter((run) => run.sourceType === "POWER_BI"), [runs]);
  const powerBiEvents = useMemo(() => events.filter((event) => event.sourceType === "POWER_BI"), [events]);
  const monitorEvents = useMemo(() => events.filter((event) => event.sourceType === "AZURE_MONITOR"), [events]);
  const marketingJobs = useMemo(() => jobs.filter((job) => job.sourceType === "MARKETING"), [jobs]);
  const marketingRuns = useMemo(() => runs.filter((run) => run.sourceType === "MARKETING"), [runs]);
  const marketingIssues = marketingJobs.filter((job) => !["SUCCEEDED", "SCHEDULED", "RUNNING"].includes(job.status));
  const synapseStats = useMemo(() => {
    const failed = synapseJobs.filter((job) => job.status === "FAILED").length;
    const longRunning = synapseJobs.filter((job) => job.status === "LONG_RUNNING").length;
    const missing = synapseJobs.filter((job) => job.status === "MISSING").length;
    const lastPoll = latestTimestamp(synapseJobs.map((job) => job.statusLastCheckedAt)) || latestTimestamp(synapseRuns.map((run) => run.lastCheckedAt));

    return {
      failed,
      longRunning,
      missing,
      lastPoll,
      activePipelines: synapseJobs.length
    };
  }, [synapseJobs, synapseRuns]);
  const acrStats = useMemo(() => {
    const failed = acrJobs.filter((job) => job.status === "FAILED").length;
    const running = acrJobs.filter((job) => job.status === "RUNNING" || job.status === "LONG_RUNNING").length;
    const lastPoll = latestTimestamp(acrJobs.map((job) => job.statusLastCheckedAt)) || latestTimestamp(acrRuns.map((run) => run.lastCheckedAt));

    return {
      activeJobs: acrJobs.length,
      failed,
      running,
      webhookEvents: acrEvents.length,
      lastPoll
    };
  }, [acrEvents, acrJobs, acrRuns]);
  const dbStats = useMemo(() => {
    const failed = dbJobs.filter((job) => job.status === "FAILED").length;
    const running = dbJobs.filter((job) => job.status === "RUNNING" || job.status === "LONG_RUNNING").length;
    const lastPoll = latestTimestamp(dbJobs.map((job) => job.statusLastCheckedAt)) || latestTimestamp(dbRuns.map((run) => run.lastCheckedAt));

    return {
      activeJobs: dbJobs.length,
      failed,
      running,
      events: dbEvents.length,
      lastPoll
    };
  }, [dbEvents, dbJobs, dbRuns]);
  const powerBiStats = useMemo(() => {
    const failed = powerBiJobs.filter((job) => job.status === "FAILED").length;
    const running = powerBiJobs.filter((job) => job.status === "RUNNING" || job.status === "LONG_RUNNING").length;
    const lastPoll = latestTimestamp(powerBiJobs.map((job) => job.statusLastCheckedAt)) || latestTimestamp(powerBiRuns.map((run) => run.lastCheckedAt));

    return {
      activeJobs: powerBiJobs.length,
      failed,
      running,
      events: powerBiEvents.length,
      lastPoll
    };
  }, [powerBiEvents, powerBiJobs, powerBiRuns]);

  if (isLoading) {
    return <StateBlock type="loading" title="Loading dashboard" message="Collecting health telemetry." />;
  }

  if (error || !summary) {
    return <StateBlock type="error" title="Dashboard unavailable" message={error || "Summary data is missing."} />;
  }

  return (
    <section className="page-stack">
      <div className="page-heading">
        <div>
          <p>Command overview</p>
          <h2>Fleet Health</h2>
        </div>
        <button className="primary-button" type="button" onClick={onOpenRuns}>
          <Database size={16} aria-hidden="true" />
          Open Runs
        </button>
      </div>

      <div className="metric-grid metric-grid--health-overview">
        <div className="metric-card-stack">
          <MetricCard label="Total Jobs" value={summary.totalJobs} status="SCHEDULED" statusLabel="Active" subtitle="Active monitors" />
          <MetricCard label="Healthy Jobs" value={summary.healthyJobs} status="SUCCEEDED" />
          <MetricCard label="Running Jobs" value={summary.runningJobs} status="RUNNING" />
          <MetricCard label="Failed Jobs" value={summary.failedJobs} status="FAILED" />
          <MetricCard label="Cancelled Jobs" value={summary.cancelledJobs} status="CANCELLED" />
        </div>

        <section className="metric-health-map-panel" aria-label="Health map overview">
          {healthMapNodes.length > 0 ? (
            <HealthMap
              nodes={healthMapNodes}
              edges={healthMapEdges}
              onOpenJob={onOpenJob}
              variant="summary"
              autoRotate
              rotationSpeed={0.0112}
              showNodeBorders={false}
            />
          ) : (
            <div className="metric-health-map-loading">
              {isHealthMapLoading ? <KineticTextLoader /> : <strong>Health Map</strong>}
              <span>{isHealthMapLoading ? "Rendering dependency graph after dashboard telemetry." : "Dependency graph will appear after telemetry loads."}</span>
            </div>
          )}
        </section>

        <div className="metric-card-stack">
          <MetricCard label="Missing Jobs" value={summary.missingJobs} status="MISSING" />
          <MetricCard label="Late Jobs" value={summary.lateJobs} status="LATE" />
          <MetricCard label="Long Running Jobs" value={summary.longRunningJobs} status="LONG_RUNNING" />
          <MetricCard label="Unknown Jobs" value={summary.unknownJobs} status="UNKNOWN" />
          <MetricCard label="Last Updated" value={formatDateTime(summary.lastUpdatedAt)} status="UNKNOWN" statusLabel="" subtitle="Latest snapshot time" />
        </div>
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <p>Active incidents</p>
              <h3>Operator Attention</h3>
            </div>
            <AlertOctagon size={18} aria-hidden="true" />
          </div>
          <div className="incident-list">
            {incidentJobs.map((job) => (
              <button className="incident-row" key={job.id} type="button" onClick={() => onOpenJob(job.id)}>
                <span>
                  <strong>{job.name}</strong>
                  <small>{job.sourceName}</small>
                </span>
                <StatusChip status={job.status} />
                <SeverityBadge severity={job.severity} />
              </button>
            ))}
          </div>
        </section>

        <MonitoringPanel
          className="synapse-panel"
          eyebrow="Synapse monitoring"
          title="Pipeline Health"
          icon={<Workflow size={18} aria-hidden="true" />}
          alertIcon={<AlertOctagon size={16} aria-hidden="true" />}
          alertMessage={
            synapseStats.failed > 0 || synapseStats.longRunning > 0 || synapseStats.missing > 0
              ? "Synapse polling has active exceptions or delayed runs."
              : undefined
          }
          stats={[
            { label: "Active pipelines", value: synapseStats.activePipelines },
            { label: "Failed", value: synapseStats.failed },
            { label: "Long running", value: synapseStats.longRunning },
            { label: "Missing", value: synapseStats.missing },
            { label: "Last Synapse poll", value: formatDateTime(synapseStats.lastPoll) }
          ]}
          runs={synapseRuns}
          emptyMessage="No Synapse runs captured."
          onOpenJob={onOpenJob}
        />

        <MonitoringPanel
          className="acr-panel"
          eyebrow="ACR monitoring"
          title="Registry and Job Health"
          icon={<Package size={18} aria-hidden="true" />}
          alertIcon={<AlertOctagon size={16} aria-hidden="true" />}
          alertMessage={
            acrStats.failed > 0 || acrStats.running > 0
              ? "ACR job monitoring has active failures or running executions."
              : undefined
          }
          stats={[
            { label: "Active jobs", value: acrStats.activeJobs },
            { label: "Failed jobs", value: acrStats.failed },
            { label: "Running jobs", value: acrStats.running },
            { label: "Webhook events", value: acrStats.webhookEvents },
            { label: "Last ACR poll", value: formatDateTime(acrStats.lastPoll) }
          ]}
          runs={acrRuns}
          emptyMessage="No ACR run history captured."
          onOpenJob={onOpenJob}
        />

        <MonitoringPanel
          className="db-panel"
          eyebrow="DB monitoring"
          title="SQL ETL Health"
          icon={<Database size={18} aria-hidden="true" />}
          alertIcon={<AlertOctagon size={16} aria-hidden="true" />}
          alertMessage={
            dbStats.failed > 0 || dbStats.running > 0
              ? "DB monitoring has active failures or running executions."
              : undefined
          }
          stats={[
            { label: "Active jobs", value: dbStats.activeJobs },
            { label: "Failed jobs", value: dbStats.failed },
            { label: "Running jobs", value: dbStats.running },
            { label: "Events", value: dbStats.events },
            { label: "Last DB poll", value: formatDateTime(dbStats.lastPoll) }
          ]}
          runs={dbRuns}
          emptyMessage="No DB monitoring runs captured."
          onOpenJob={onOpenJob}
        />

        <MonitoringPanel
          className="powerbi-panel"
          eyebrow="Power BI monitoring"
          title="Semantic Model Health"
          icon={<BarChart3 size={18} aria-hidden="true" />}
          alertIcon={<AlertOctagon size={16} aria-hidden="true" />}
          alertMessage={
            powerBiStats.failed > 0 || powerBiStats.running > 0
              ? "Power BI monitoring has active failures or refreshes."
              : undefined
          }
          stats={[
            { label: "Active models", value: powerBiStats.activeJobs },
            { label: "Failed jobs", value: powerBiStats.failed },
            { label: "Running jobs", value: powerBiStats.running },
            { label: "Events", value: powerBiStats.events },
            { label: "Last PBI poll", value: formatDateTime(powerBiStats.lastPoll) }
          ]}
          runs={powerBiRuns}
          emptyMessage="No Power BI refresh runs captured."
          onOpenJob={onOpenJob}
        />

        <MonitoringPanel
          className="marketing-monitoring-panel"
          eyebrow="Marketing monitoring"
          title="Collection and Data Health"
          icon={<BarChart3 size={18} aria-hidden="true" />}
          alertIcon={<AlertOctagon size={16} aria-hidden="true" />}
          alertMessage={marketingIssues.length ? marketingIssues.map((job) => `${job.name}: ${job.statusReason || job.status}`).join(" ") : undefined}
          stats={[
            { label: "Active monitors", value: marketingJobs.length },
            { label: "Exceptions", value: marketingIssues.length },
            { label: "Running", value: marketingJobs.filter((job) => job.status === "RUNNING").length },
            { label: "Last Marketing poll", value: formatDateTime(latestTimestamp(marketingJobs.map((job) => job.statusLastCheckedAt))) }
          ]}
          runs={marketingRuns}
          emptyMessage="No Marketing execution history captured."
          onOpenJob={onOpenJob}
        />

        <section className="panel">
          <div className="panel-heading">
            <div>
              <p>Recent execution</p>
              <h3>Run Timeline</h3>
            </div>
            <TimerReset size={18} aria-hidden="true" />
          </div>
          <div className="timeline-list">
            {recentRuns.map((run) => (
              <button className="timeline-row" key={run.id} type="button" onClick={() => onOpenJob(run.jobId)}>
                <i className={`timeline-dot status-${run.status.toLowerCase().replace("_", "-")}`} />
                <span>
                  <strong>{run.jobName}</strong>
                  <small>{formatDateTime(run.lastCheckedAt)} - {run.owner}</small>
                </span>
                <StatusChip status={run.status} />
              </button>
            ))}
          </div>
        </section>

        <section className="panel events-panel">
          <div className="panel-heading">
            <div>
              <p>Telemetry events</p>
              <h3>Signals</h3>
            </div>
            <Bell size={18} aria-hidden="true" />
          </div>
          <div className="event-list">
            {events.slice(0, 8).map((event) => (
              <article className="event-row" key={event.id}>
                <div>
                  <strong>{event.eventType}</strong>
                  <span>{event.message}</span>
                </div>
                <StatusChip status={event.status} />
              </article>
            ))}
            {events.length === 0 && <div className="empty-inline">No telemetry events captured.</div>}
          </div>
        </section>

        <section className="panel alert-panel">
          <div className="panel-heading">
            <div>
              <p>Azure alert</p>
              <h3>Alerts</h3>
            </div>
            <ShieldAlert size={18} aria-hidden="true" />
          </div>
          {monitorEvents.length === 0 ? (
            <div className="empty-inline">No Azure alerts captured.</div>
          ) : (
            <div className="alert-signal-list">
              {monitorEvents.slice(0, 5).map((event) => (
                <button className="alert-signal-row" key={event.id} type="button" onClick={() => event.jobId && onOpenJob(event.jobId)}>
                  <span>
                    <strong>{event.sourceName}</strong>
                    <small>{event.message}</small>
                  </span>
                  <StatusChip status={event.status} />
                  <SeverityBadge severity={event.severity} />
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
